// Caseload — searchable + filterable list of students.
// Iter 23 additions:
//   - Teacher filter (sort by classroom teacher)
//   - View toggle: Card / List
//   - Add Student modal (POST /api/students)
//   - Role-aware service-area chips (driven by current provider's role)
// Iter 34 Pass B/C cleanup (Apr 30, 2026):
//   - Top filter chips driven by canonical role-scoped service taxonomy
//   - Cards/rows show canonical services[] (label + sub_areas tooltip)
//   - Readable IDs surfaced on each card/row
//   - Service-area filter became a domain filter (canonical-aware backend)
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= on student-row clicks so
// admin's read-only workspace context doesn't drop when navigating
// to a student detail page from their shadowed Caseload.
import useShadowNavigate from "@/lib/useShadowNavigate";
import { createPortal } from "react-dom";
import {
  MagnifyingGlass,
  Target,
  ArrowRight,
  Warning,
  CalendarBlank,
  SquaresFour,
  ListBullets,
  Plus,
  X,
  Clock,
  UploadSimple,
  CaretUp,
  CaretDown,
  DownloadSimple,
} from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import api, { prettyApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useCurrentProviderStudents from "@/hooks/useCurrentProviderStudents";
import AssistantsCard from "@/components/caseload/AssistantsCard";
import AssistantAssignmentChip, { clearMyAssistantsCache } from "@/components/caseload/AssistantAssignmentChip";
import AssistantCoveredPill from "@/components/caseload/AssistantCoveredPill";
import CoverageBadge from "@/components/caseload/CoverageBadge";
// FP3-058 — Caseload bulk-select Archive + soft-Delete toolbar &
// modals. Permission gating is enforced server-side per row; the
// toolbar only gates the UI affordance.
import {
  BulkActionsToolbar,
  ArchiveSelectedModal,
  DeleteSelectedModal,
} from "@/components/caseload/CaseloadBulkActions";
// FP3-064 — Bulk assign students to a school/campus from Caseload.
import AssignCampusDialog from "@/components/student/AssignCampusDialog";
import { nativeOptions } from "@/lib/nativeOptions";
import EligibilityEditor, { primaryEligibilityKey } from "@/components/student/EligibilityEditor";
// FP3-081 family-name semantics ("Adelina Ramos Martinez" sorts under
// Ramos) now run server-side for every Caseload sort — see FP3-100.
// FP3-092/FP3-100 — shared canonical attendance normalizer (legacy
// half_day_am / half_day_pm / variable → am / pm / partial).
import { normalizeAttendancePattern } from "@/lib/studentProfileTaxonomy";
// FP3-095 — Shared Areas of Impact labels + chip colors (same module
// the Admin Students table uses) so both surfaces render identical
// canonical chips from the backend's `impact_domains` projection.
import { DOMAIN_LABELS, DOMAIN_COLORS } from "@/lib/impactDomains";
// FP3-097 — shared teacher-name derivation (one source of truth).
import { deriveTeacherDisplay } from "@/lib/teacherIdentity";

// FP3-035B correction — Kindergarten = "0" platform-wide.
const GRADES = Array.from({ length: 13 }, (_, i) => String(i));

// FP3-095 — The Provider Caseload's student-facing column shows the
// student's canonical Areas of Impact (`impact_domains`, stamped by
// the backend via the SAME resolver the Admin Students page uses).
// It intentionally shows ALL of the student's impact areas — not just
// the signed-in provider's discipline — for multidisciplinary context.
// The provider's own discipline scope indicator above the table
// (e.g. "SLP Domains · Speech-Language") remains separate.
function studentImpactDomains(student) {
  return Array.isArray(student?.impact_domains) ? student.impact_domains : [];
}

// FP3-095 — Compact Areas of Impact chip cluster. Shows up to
// `max` chips + a "+N" overflow marker whose tooltip lists the
// remaining values; renders an em dash when none are recorded.
// Chip styling matches the Admin Students table.
function ImpactChips({ student, max = 2, testidPrefix }) {
  const impacts = studentImpactDomains(student);
  if (impacts.length === 0) {
    return (
      <span
        data-testid={`${testidPrefix}-empty-${student.id}`}
        className="text-[10px] text-[#9B9890] italic"
      >
        —
      </span>
    );
  }
  const visible = impacts.slice(0, max);
  const overflow = impacts.slice(max);
  return (
    <>
      {visible.map((d) => {
        const c = DOMAIN_COLORS[d] || { bg: "bg-[#F3F1EC]", fg: "text-[#6B7280]" };
        return (
          <Badge
            key={d}
            data-testid={`${testidPrefix}-${student.id}-${d}`}
            className={`text-[10px] font-normal ${c.bg} ${c.fg} hover:${c.bg}`}
            title="From Areas of Impact under Eligibility"
          >
            {DOMAIN_LABELS[d] || d}
          </Badge>
        );
      })}
      {overflow.length > 0 && (
        <span
          data-testid={`${testidPrefix}-more-${student.id}`}
          className="text-[10px] text-[#6B7280] self-center"
          title={overflow.map((d) => DOMAIN_LABELS[d] || d).join(", ")}
        >
          +{overflow.length}
        </span>
      )}
    </>
  );
}

// FP3-096 — designation → tooltip display label.
const designationLabel = (d) => {
  const key = (d || "").toLowerCase();
  if (key === "primary") return "Primary";
  if (key === "secondary") return "Secondary";
  return "Additional";
};

// FP3-096 — Canonical Eligibility chip. Same source, abbreviations and
// primary-eligibility resolution as the Admin Students table (backend
// stamps `eligibility_label` / `eligibility_abbr` / `eligibility_display`
// via the shared resolver). Primary abbr chip + "+N" for additional
// entries; tooltip lists every category with its designation.
// Eligibility (IDEA category) stays separate from Areas of Impact.
function EligibilityChip({ student, testidPrefix }) {
  const entries = Array.isArray(student?.eligibility_display)
    ? student.eligibility_display
    : [];
  if (!student?.eligibility_label) {
    return (
      <span
        data-testid={`${testidPrefix}-unset-${student.id}`}
        className="text-[10px] text-[#B85C5C] italic"
        title="Eligibility category not set"
      >
        unset
      </span>
    );
  }
  const tip = entries.length > 1
    ? entries
        .map((e) => `${e.label || e.category} (${designationLabel(e.designation)})`)
        .join(" · ")
    : student.eligibility_label;
  return (
    <span className="inline-flex items-center gap-1">
      <Badge
        data-testid={`${testidPrefix}-${student.id}`}
        className="bg-[#EEF1F6] text-[#3F4756] hover:bg-[#EEF1F6] text-[10px] font-medium"
        title={tip}
      >
        {student.eligibility_abbr || student.eligibility_label}
      </Badge>
      {entries.length > 1 && (
        <span
          data-testid={`${testidPrefix}-extra-${student.id}`}
          className="text-[9px] text-[#6B7280] font-semibold tabular-nums"
          title={tip}
        >
          +{entries.length - 1}
        </span>
      )}
    </span>
  );
}

function daysFromNow(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
}

// FP3-093 — the signed-in provider's own assignment status on a
// student row. Missing row / missing status behaves as "active".
function myAssignmentStatus(student, providerId) {
  if (!providerId) return "active";
  const row = (student.team_assignments || []).find(
    (t) => t && t.provider_id === providerId
  );
  return ((row?.assignment_status || "active") + "").toLowerCase();
}

const MY_SERVICE_CHIP = {
  graduated: {
    label: "Graduated · my service",
    cls: "bg-[#FDF0DC] text-[#8A4B08] border border-[#E8B04B]",
  },
  archived: {
    label: "Archived · my caseload",
    cls: "bg-[#F2E7E5] text-[#7A3B3B] border border-[#CFA9A4]",
  },
  monitoring: {
    label: "Monitoring · my service",
    cls: "bg-[#E8F0FA] text-[#1D4E89] border border-[#B9D2EC]",
  },
};

export default function CaseloadList() {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    campus: "",
    grade: "",
    status: "active",
    domain: "",     // Pass B canonical domain filter (replaces legacy service_area)
    teacher: "",
    // FP3-093 — "My Service Status" (the caller's own assignment
    // status per student). "" = default active-workflow scope.
    myService: "",
    // FP3-110 — "Service Coverage" filter: "" (All My Caseload) |
    // "direct" (Directly Serviced by Me) | "assistants" (Assigned to
    // My Assistants). Composes with every other filter + the search
    // string; server pipeline applies it AFTER assistant stamping so
    // the CSV export matches the on-screen list byte-for-byte.
    serviceCoverage: "",
  });
  const [activeChip, setActiveChip] = useState("");
  // Pass B — role-aware service taxonomy drives the filter chips. We keep
  // the chips as canonical AZ services (verbatim labels), but the filter
  // collapses each chip to its domain so a student tagged
  // {key:"speech_language"} matches the "Speech/Language" / "Listening
  // Comprehension" / "Oral Expression" SLP-default chips uniformly.
  const [taxonomy, setTaxonomy] = useState(null);
  // Iter 32 — Persist preferred view mode across navigation/reload. Falls
  // back to "card" for first-time users.
  const [view, setView] = useState(() => {
    try {
      const stored = localStorage.getItem("frame.caseload.viewMode");
      return stored === "list" || stored === "card" ? stored : "card";
    } catch {
      return "card";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("frame.caseload.viewMode", view);
    } catch {
      /* localStorage may be unavailable in private/SSR — ignore */
    }
  }, [view]);
  const [sortBy, setSortBy] = useState("name"); // "name" | "teacher" | "grade" | "pattern"
  // FP3-081 — Direction toggle for the primary "Sort: Last name" option.
  // Other sort modes (teacher/grade/pattern) also honor this as the
  // family-name tie-break direction so the secondary order isn't
  // surprising after the user flips the arrow.
  const [sortDir, setSortDir] = useState("asc"); // "asc" (A→Z) | "desc" (Z→A)
  const [addOpen, setAddOpen] = useState(false);
  // FP3-110 — CSV export state. `Export CSV` mirrors the Admin
  // students behaviour but calls the provider-scoped
  // /caseload/export.csv endpoint. Disabled while the list is
  // (re-)loading so the file the user gets ALWAYS matches the on-
  // screen list byte-for-byte.
  const [exporting, setExporting] = useState(false);

  // FP3-075E — Setup-checklist deep link: `/caseload?add=1` auto-
  // opens the Add Student modal. Strip the param after activating so
  // a manual close + page refresh doesn't keep reopening the modal.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("add") === "1") {
      setAddOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("add");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  // FP3-043 Phase A follow-up #7 — Org-scoped schools list powers
  // BOTH the campus filter and the Add Student campus dropdown.
  // No demo campuses ever surface here.
  const [schools, setSchools] = useState([]);
  const [schoolsLoaded, setSchoolsLoaded] = useState(false);
  useEffect(() => {
    api.listSchools()
      .then((r) => {
        const rows = Array.isArray(r?.data) ? r.data : [];
        const cleaned = rows
          .filter((s) => s && s.id && (s.name || "").trim())
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setSchools(cleaned);
        setSchoolsLoaded(true);
      })
      .catch(() => {
        setSchools([]);
        setSchoolsLoaded(true);
      });
  }, []);
  const navigate = useShadowNavigate();
  // Keep the raw useNavigate around in case other call sites need
  // bare navigation (none currently — every navigate(...) in this
  // file should respect the shadow context). The hook degrades to
  // plain navigation when ?as_provider= is absent, so this swap is
  // a safe no-op for regular providers.
  void useNavigate;

  // Iter 45g — single source of truth for "students on the current
  // provider's caseload". Provider scoping happens inside the hook.
  // Pass server-side filters/search so the server can narrow before
  // we hydrate per-student goal counts.
  // FP3-064 — Campus filter now sends `school_id` instead of the
  // legacy `campus` display string. This is robust to a school
  // rename and — more importantly — catches every student whose
  // `school_id` is set but whose `campus` text field is empty or
  // stale (the production bug that left Pete's "Primary Workspace"
  // filter at 0 students). Keep `filters.campus` named the same so
  // the rest of the FE state code keeps working.
  const studentParams = useMemo(() => {
    const p = {};
    if (filters.campus) p.school_id = filters.campus;
    if (filters.grade) p.grade = filters.grade;
    if (filters.status) p.status = filters.status;
    if (filters.domain) p.domain = filters.domain;
    if (filters.myService) p.my_service_status = filters.myService;
    if (filters.serviceCoverage) p.service_coverage = filters.serviceCoverage;
    if (search) p.search = search;
    // FP3-100 — Backend-supported sorting for EVERY sortable column
    // (name / teacher / grade / eligibility / pattern / commitment /
    // campus / assistant). The server orders the full filtered result
    // set before any cap, so the FE never sorts only the rendered
    // rows. `sort_dir` also drives the family-name tie-break.
    p.sort_by = sortBy;
    p.sort_dir = sortDir;
    return p;
  }, [filters.campus, filters.grade, filters.status, filters.domain, filters.myService, filters.serviceCoverage, search, sortBy, sortDir]);

  const { provider, students, loading, refetch } = useCurrentProviderStudents(studentParams);

  // FP3-058 — Caseload bulk-select state.
  // Capability check: admin family + solo provider owners may mutate
  // the caseload via the bulk toolbar. Assistants + school-team
  // providers see a read-only caseload with no checkboxes or
  // toolbar. Server enforces the same rules per row.
  //
  // FP3-062 follow-up (Jun 1, 2026) — Use the backend-computed
  // `is_solo_provider` flag from auth context instead of re-deriving
  // from `user.account_type`. Real solo providers minted through
  // Request Access carry the flag on the org row only, so the
  // previous user-row-only check silently denied them the bulk
  // affordance in production.
  // FP3-079C — A solo workspace owner whose discipline is SLPA /
  // COTA / PTA carries `provider_role_type: "assistant"` but still
  // OWNS the workspace. The blanket assistant exclusion used here
  // previously hid the whole bulk UI from them in production.
  // `isSoloWorkspaceOwner` (auth context) excludes only INVITED
  // assistants (those with a supervising_provider_id).
  const { user, isSoloWorkspaceOwner } = useAuth();
  const role = (user?.permission_role || "").toLowerCase();
  const isAdminFamily = role === "super_admin" || role === "admin" || role === "org_admin";
  const canBulkAct = isAdminFamily || isSoloWorkspaceOwner;
  // FP3-110 — Invited assistants (permission_role=provider,
  // provider_role_type=assistant, NOT a solo workspace owner) never
  // see the Service Coverage dropdown — their caseload is already
  // scoped to the students they personally service. Solo SLPA/COTA/
  // PTA owners keep the dropdown because they can supervise their
  // own assistants.
  const isAssistantViewer =
    (user?.provider_role_type || "").toLowerCase() === "assistant"
    && !isSoloWorkspaceOwner;

  const [selectedIds, setSelectedIds] = useState([]);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // FP3-064 — Bulk assign campus dialog state.
  const [assignCampusOpen, setAssignCampusOpen] = useState(false);

  const toggleSelect = useCallback((id) => {
    if (!canBulkAct) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, [canBulkAct]);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const onBulkSuccess = useCallback(() => {
    setSelectedIds([]);
    refetch?.();
  }, [refetch]);

  // FP3-029B6C — Bust the cached /auth/me/assistants payload so the
  // AssistantsCard caseload counts refresh after each assignment.
  const handleAssistantChange = useCallback(() => {
    clearMyAssistantsCache();
    refetch?.();
  }, [refetch]);

  // Pass B — load role-scoped taxonomy once provider is known
  useEffect(() => {
    if (!provider) return;
    api.getServicesTaxonomy({ role: provider.role || "" })
      .then((r) => setTaxonomy(r.data))
      .catch(() => setTaxonomy(null));
  }, [provider]);

  // Iter 45g — student fetch lives inside `useCurrentProviderStudents`.
  // Search debouncing is now driven by the server-side `search` param
  // change forcing a refetch via the hook's params dep.

  // Role-scoped service chips collapsed to one chip per DOMAIN, labeled
  // with the domain's display label. This is intentional — within a role,
  // filtering across "Speech/Language" / "Listening Comprehension" /
  // "Oral Expression" returns the same students (same domain), so showing
  // one canonical "Speech-Language" chip is honest and uncluttered.
  const scopedServices = taxonomy?.scoped?.services || [];
  const chipServices = useMemo(() => {
    const seen = new Set();
    const out = [];
    const domainLabel = {};
    for (const d of (taxonomy?.domains || [])) {
      domainLabel[d.key] = d.label;
    }
    for (const s of scopedServices) {
      if (s.domain && !seen.has(s.domain)) {
        seen.add(s.domain);
        out.push({
          key: s.domain,
          label: domainLabel[s.domain] || s.label,
          domain: s.domain,
        });
      }
    }
    return out;
  }, [scopedServices, taxonomy]);

  // Teacher list derived from currently-loaded students.
  const teachers = useMemo(() => {
    const set = new Set();
    students.forEach((s) => s.teacher && set.add(s.teacher));
    return Array.from(set).sort();
  }, [students]);

  const filteredStudents = useMemo(() => {
    let arr = students;
    // Teacher filter stays client-side (derived from loaded rows);
    // filtering preserves the server-applied order.
    if (filters.teacher) arr = arr.filter((s) => s.teacher === filters.teacher);
    // FP3-100 — sorting now happens server-side for every sort key
    // (see studentParams). The array arrives in canonical order for
    // BOTH List and Card views; no client re-sort.
    return arr;
  }, [students, filters.teacher]);

  // FP3-100 — single canonical sort state. Header clicks and the
  // Sort dropdown/direction control all drive the same two states.
  const handleHeaderSort = useCallback((key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  }, [sortBy]);

  const handleChip = (svc) => {
    // Pass B — chips are canonical services; toggle filters by their
    // domain so all sub-areas under that domain match in one click.
    if (activeChip === svc.key) {
      setActiveChip("");
      setFilters((f) => ({ ...f, domain: "" }));
    } else {
      setActiveChip(svc.key);
      setFilters((f) => ({ ...f, domain: svc.domain }));
    }
  };

  // FP3-110 — Download the current filtered + sorted caseload as CSV.
  // Snapshots the SAME `studentParams` the list uses so the server's
  // response mirrors the on-screen list. Rejects while loading /
  // already exporting to avoid a mid-refetch export drift.
  const exportCsv = async () => {
    if (loading || exporting) return;
    setExporting(true);
    try {
      const params = { ...studentParams };
      // Prevent any CDN/proxy from replaying a previous export.
      params._export_nonce = Date.now();
      const r = await api.caseloadExportCsv(params);
      const cd = r.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename =
        (m && m[1]) ||
        `my-caseload-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(
        new Blob([r.data], { type: "text/csv;charset=utf-8" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const n = Number(r.headers?.["x-student-count"]);
      const count = Number.isFinite(n) && n >= 0 ? n : filteredStudents.length;
      toast.success(
        `${count} ${count === 1 ? "student" : "students"} exported to CSV.`
      );
    } catch (e) {
      toast.error(
        `Export failed — no file was downloaded. ${prettyApiError(e)}`
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="px-6 py-6 max-w-[1600px] xl:max-w-[1760px] 2xl:max-w-none animate-fade-in" data-testid="caseload-page">
      {/* FP3-112A — Left-aligned title + action toolbar. The action
          cluster now sits UNDER the title (not pushed to the extreme
          right via justify-between), so at 1440-1760px shell widths
          Add Student is never clipped or stranded far from the eye.
          `flex-wrap` still lets the toolbar wrap to two rows at 1024px
          and below. Permission rules for Import / Export / Add are
          unchanged. */}
      <div className="mb-4" data-testid="caseload-header-row">
        <div className="min-w-0 mb-3">
          <h1
            className="text-3xl font-semibold text-[#1F2937] heading-font tracking-tight leading-tight"
            data-testid="caseload-title"
          >
            Caseload
          </h1>
          <p className="text-sm text-[#6B7280] mt-0.5" data-testid="caseload-subtitle">
            {filteredStudents.length} student{filteredStudents.length === 1 ? "" : "s"}
            {provider?.role && (
              <span className="ml-2 text-[#9B9890]">· {provider.role}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="caseload-header-actions">
          <ViewToggle view={view} setView={setView} />
          {/* FP3-035B-2 — Provider CSV import access. Solo / school-
              team providers can import their caseload without an
              admin handoff. Routes to the shared ImportStudentsPage
              (mode=solo for solos, mode=provider for school-team). */}
          <Link to="/import" data-testid="caseload-import-students-btn">
            <Button
              variant="outline"
              className="rounded-lg h-9 text-sm border-[#d4d2cc] text-[#1F2937] hover:bg-[#F4F2EC]"
            >
              <UploadSimple size={15} weight="bold" className="mr-1.5" />
              Import Students
            </Button>
          </Link>
          {/* FP3-110 — Export the CURRENT filtered + sorted list to
              CSV. Every provider role can export their own caseload
              (assistants export the students they personally service;
              supervising primaries export the composition selected by
              the Service Coverage dropdown). Disabled while loading
              so the file NEVER drifts from the on-screen list. */}
          <Button
            data-testid="caseload-export-csv-btn"
            onClick={exportCsv}
            disabled={loading || exporting || filteredStudents.length === 0}
            title={
              loading
                ? "Updating list…"
                : filteredStudents.length === 0
                  ? "No students match the current filters."
                  : "Export the current filtered caseload"
            }
            variant="outline"
            className="rounded-lg h-9 text-sm border-[#1E3F33]/30 text-[#1E3F33] hover:bg-[#E8F0EB] disabled:opacity-40"
          >
            <DownloadSimple size={15} weight="bold" className="mr-1.5" />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          <Button
            data-testid="add-student-btn"
            onClick={() => setAddOpen(true)}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-9 text-sm"
          >
            <Plus size={15} weight="bold" className="mr-1.5" />
            Add Student
          </Button>
        </div>
      </div>

      {/* FP3-029B6B — Assistants card surfaced for primary providers
          who supervise active assistants. Renders only when
          applicable so non-supervising providers never see it. */}
      <AssistantsCard />

      {/* Search & Filters */}
      {/* FP3-100 — Aligned filter/sort toolbar. Labeled 5-column grid
          on desktop (Row 1: Search / Campus / Grade / Teacher /
          Student Status · Row 2: My Service Status / Quick View /
          Reset / Sort+Direction), wrapping to 3-2-1 columns at
          smaller widths. Every control is h-9 + w-full so heights and
          labels align. Student Status vs My Service Status behavior
          is UNCHANGED.
          FP3-112A — Cap the toolbar at 1400px so at 1600-1760px
          shell widths the filter controls stay a readable size
          (~260px each) instead of ballooning to 300+px. Card stays
          left-aligned to the shell edge; extra space becomes empty
          margin on the right (matching the compact table). */}
      <div className="space-y-3 mb-4">
        <div
          data-testid="caseload-toolbar"
          className="bg-white border border-[#E5E3DB] rounded-2xl p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-x-3 gap-y-3 items-end max-w-[1400px]"
        >
          <ToolbarField label="Search Students" htmlFor="caseload-search-input">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
              <Input
                id="caseload-search-input"
                data-testid="caseload-search"
                placeholder="Search students..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 w-full bg-white border-[#E5E3DB] focus:ring-[#8FA998]/50 focus:border-[#8FA998] rounded-lg"
              />
            </div>
          </ToolbarField>
          <ToolbarField label="Campus" htmlFor="filter-campus-trigger">
            <Select
              value={filters.campus || "all"}
              onValueChange={(v) => setFilters((f) => ({ ...f, campus: v === "all" ? "" : v }))}
            >
              <SelectTrigger id="filter-campus-trigger" data-testid="filter-campus" className="w-full h-9 bg-white border-[#E5E3DB] rounded-lg">
                <SelectValue placeholder="All Campuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Campuses</SelectItem>
                {/* FP3-043 Phase A follow-up #7 — Real org-scoped
                    schools only. No hardcoded demo campuses.
                    FP3-064 — Value is `school.id` (canonical) not
                    `school.name`. CaseloadList's studentParams memo
                    translates `filters.campus` → `school_id=` on
                    the GET /students request. */}
                {schools.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ToolbarField>
          <ToolbarField label="Grade" htmlFor="filter-grade-trigger">
            <Select
              value={filters.grade || "all"}
              onValueChange={(v) => setFilters((f) => ({ ...f, grade: v === "all" ? "" : v }))}
            >
              <SelectTrigger id="filter-grade-trigger" data-testid="filter-grade" className="w-full h-9 bg-white border-[#E5E3DB] rounded-lg">
                <SelectValue placeholder="All Grades" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Grades</SelectItem>
                {GRADES.map((g) => (
                  <SelectItem key={g} value={g}>
                    Grade {g}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ToolbarField>
          <ToolbarField label="Teacher" htmlFor="filter-teacher-trigger">
            <Select
              value={filters.teacher || "all"}
              onValueChange={(v) => setFilters((f) => ({ ...f, teacher: v === "all" ? "" : v }))}
            >
              <SelectTrigger id="filter-teacher-trigger" data-testid="filter-teacher" className="w-full h-9 bg-white border-[#E5E3DB] rounded-lg">
                <SelectValue placeholder="All Teachers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teachers</SelectItem>
                {teachers.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ToolbarField>
          {/* FP3-093A — labelled STUDENT STATUS (org-wide) control. */}
          <ToolbarField label="Student Status" htmlFor="filter-status-trigger">
            <Select
              value={filters.status === "active_therapy" ? "all" : (filters.status || "all")}
              onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}
            >
              <SelectTrigger id="filter-status-trigger" data-testid="filter-status" className="w-full h-9 bg-white border-[#E5E3DB] rounded-lg">
                <SelectValue placeholder="Student Status" />
              </SelectTrigger>
              <SelectContent>
                {/* FP3-093 — Canonical Student Status taxonomy shared
                    with the Admin Students filter: All / IEP / RTI /
                    Monitoring / Graduated / Archived. "Active Therapy"
                    is the derived Quick View pill (IEP + RTI), not a
                    stored status. */}
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">IEP</SelectItem>
                <SelectItem value="rti">RTI</SelectItem>
                <SelectItem value="monitoring">Monitoring</SelectItem>
                <SelectItem value="graduated">Graduated</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </ToolbarField>
          {/* FP3-093A — labelled MY SERVICE STATUS: the signed-in
              provider's own relationship with each student,
              independent of the org-wide Student Status. Selecting a
              historical value (Graduated / Archived) auto-clears the
              Active Therapy quick view and resets Student Status to
              All so the two filters never contradict each other. */}
          <ToolbarField label="My Service Status" htmlFor="filter-my-service-trigger">
            <Select
              value={filters.myService || "default"}
              onValueChange={(v) =>
                setFilters((f) => {
                  const myService = v === "default" ? "" : v;
                  const next = { ...f, myService };
                  if (myService === "graduated" || myService === "archived") {
                    next.status = "";
                  } else if (f.status === "active_therapy") {
                    next.status = "active_therapy";
                  }
                  return next;
                })
              }
            >
              <SelectTrigger id="filter-my-service-trigger" data-testid="filter-my-service" className="w-full h-9 bg-white border-[#E5E3DB] rounded-lg">
                <SelectValue placeholder="My Service Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Current (active + monitoring)</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="monitoring">Monitoring</SelectItem>
                <SelectItem value="graduated">Graduated</SelectItem>
                <SelectItem value="archived">Archived from my caseload</SelectItem>
                <SelectItem value="all">All relationships</SelectItem>
              </SelectContent>
            </Select>
          </ToolbarField>
          {/* FP3-110 — Service Coverage filter (Provider Caseload
              only). Lets a supervising provider narrow the list to
              students they service DIRECTLY, or to students their
              assistants cover. Hidden for assistants — their caseload
              is already scoped to their own directly-assigned
              students, so the dropdown would be meaningless. */}
          {!isAssistantViewer && (
            <ToolbarField label="Service Coverage" htmlFor="filter-service-coverage-trigger">
              <Select
                value={filters.serviceCoverage || "all"}
                onValueChange={(v) =>
                  setFilters((f) => ({
                    ...f,
                    serviceCoverage: v === "all" ? "" : v,
                  }))
                }
              >
                <SelectTrigger
                  id="filter-service-coverage-trigger"
                  data-testid="filter-service-coverage"
                  className="w-full h-9 bg-white border-[#E5E3DB] rounded-lg"
                >
                  <SelectValue placeholder="All My Caseload" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="service-coverage-option-all">
                    All My Caseload
                  </SelectItem>
                  <SelectItem value="direct" data-testid="service-coverage-option-direct">
                    Directly Serviced by Me
                  </SelectItem>
                  <SelectItem value="assistants" data-testid="service-coverage-option-assistants">
                    Assigned to My Assistants
                  </SelectItem>
                </SelectContent>
              </Select>
            </ToolbarField>
          )}
          {/* FP3-070 / FP3-093 — Active Therapy is a QUICK VIEW
              (IEP + RTI), not a canonical status — hence its own
              labelled cell. Never combines with Graduated/Archived
              My-Service views (FP3-093A). */}
          <ToolbarField label="Quick View">
            <button
              type="button"
              data-testid="quick-view-active-therapy"
              title="Quick view: Active Therapy = IEP + RTI (the Wizard's scheduling-eligible set)"
              onClick={() =>
                setFilters((f) =>
                  f.status === "active_therapy"
                    ? { ...f, status: "" }
                    : { ...f, status: "active_therapy", myService: "" }
                )
              }
              className={`w-full h-9 px-3 rounded-full text-xs font-medium border transition-colors ${
                filters.status === "active_therapy"
                  ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                  : "bg-white text-[#1E3F33] border-[#E5E3DB] hover:bg-[#F5F3EE]"
              }`}
            >
              Active Therapy
            </button>
          </ToolbarField>
          {/* FP3-093A / FP3-100 — one-click reset, permanently seated
              in the grid (disabled when nothing to clear) so it never
              floats or reflows the toolbar. */}
          <ToolbarField label="Reset">
            <button
              type="button"
              data-testid="clear-filters"
              disabled={
                !(filters.campus || filters.grade || filters.teacher ||
                  filters.domain || filters.myService || filters.serviceCoverage ||
                  search || filters.status !== "active")
              }
              onClick={() => {
                setFilters({
                  campus: "", grade: "", status: "active",
                  domain: "", teacher: "", myService: "",
                  serviceCoverage: "",
                });
                setSearch("");
              }}
              className="w-full h-9 px-3 rounded-lg text-xs font-medium text-[#6B7280] border border-[#E5E3DB] hover:bg-[#F5F3EE] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
            >
              Clear filters
            </button>
          </ToolbarField>
          {/* FP3-100 — Sort control cluster: dropdown + direction stay
              together in one cell and mirror the table headers (one
              canonical sort state). */}
          <ToolbarField label="Sort by" htmlFor="sort-by-trigger" className="sm:col-span-2">
            <div className="flex items-center gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger id="sort-by-trigger" data-testid="sort-by" className="w-full flex-1 h-9 bg-white border-[#E5E3DB] rounded-lg">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Sort: Last name</SelectItem>
                  <SelectItem value="grade">Sort: Grade</SelectItem>
                  <SelectItem value="eligibility" data-testid="sort-eligibility-option">Sort: Eligibility</SelectItem>
                  <SelectItem value="pattern">Sort: Pattern</SelectItem>
                  <SelectItem value="commitment" data-testid="sort-commitment-option">Sort: Commitment</SelectItem>
                  <SelectItem value="teacher">Sort: Teacher</SelectItem>
                  <SelectItem value="campus" data-testid="sort-campus-option">Sort: Campus</SelectItem>
                  <SelectItem value="assistant" data-testid="sort-assistant-option">Sort: Assistant</SelectItem>
                </SelectContent>
              </Select>
              {/* FP3-081 — Direction toggle. Independent of the sort-by
                  dropdown so users can flip A→Z / Z→A for any mode. */}
              <button
                type="button"
                data-testid="sort-direction-toggle"
                aria-label={sortDir === "asc" ? "Sort direction: A to Z (click to reverse)" : "Sort direction: Z to A (click to reverse)"}
                title={sortDir === "asc" ? "A → Z (click to reverse)" : "Z → A (click to reverse)"}
                onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                className="h-9 px-3 shrink-0 rounded-lg border border-[#E5E3DB] bg-white text-[#1E3F33] text-sm font-medium hover:bg-[#F5F3EE] transition-colors flex items-center gap-1"
              >
                <span data-testid="sort-direction-label">
                  {sortDir === "asc" ? "A → Z" : "Z → A"}
                </span>
              </button>
            </div>
          </ToolbarField>
        </div>

        <div className="flex flex-wrap gap-2 items-center" data-testid="service-area-chips">
          {chipServices.length > 0 && (
            <span className="text-[10px] uppercase tracking-wider text-[#9B9890] font-semibold mr-1">
              {provider?.role ? `${provider.role} domains` : "Service domains"}
            </span>
          )}
          {chipServices.map((svc) => (
            <button
              key={svc.key}
              data-testid={`chip-${svc.domain}`}
              onClick={() => handleChip(svc)}
              title={`Filter by ${svc.label} (rolls up to the ${svc.domain} domain)`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                activeChip === svc.key
                  ? "bg-[#1E3F33] text-white"
                  : "bg-[#E1EAE4] text-[#1E3F33] hover:bg-[#d5e5d9]"
              }`}
            >
              {svc.label}
            </button>
          ))}
          {filters.domain && (
            <button
              data-testid="chip-clear"
              onClick={() => { setActiveChip(""); setFilters((f) => ({ ...f, domain: "" })); }}
              className="text-[11px] text-[#3F4756] hover:text-[#1E3F33] underline ml-1"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* FP3-058 — Bulk actions toolbar. Sticky-top, renders only
          when ≥1 student selected. Hidden entirely for non-mutating
          roles (assistant / school-team provider). */}
      {canBulkAct && (
        <BulkActionsToolbar
          selectedIds={selectedIds}
          onClearSelection={clearSelection}
          onArchive={() => setArchiveOpen(true)}
          onDelete={() => setDeleteOpen(true)}
          onAssignCampus={() => setAssignCampusOpen(true)}
        />
      )}

      {/* FP3-058 — Select-all-visible affordance. Only renders when
          the caller can mutate and there are visible students. */}
      {canBulkAct && !loading && filteredStudents.length > 0 && (
        <div
          data-testid="caseload-bulk-select-all-bar"
          className="flex items-center gap-2 mb-3 text-[11.5px] text-[#4B5563]"
        >
          <Checkbox
            data-testid="caseload-bulk-select-all"
            checked={
              filteredStudents.length > 0
              && filteredStudents.every((s) => selectedIds.includes(s.id))
            }
            onCheckedChange={(checked) => {
              if (checked) {
                setSelectedIds(filteredStudents.map((s) => s.id));
              } else {
                setSelectedIds([]);
              }
            }}
            className="border-[#D4D2CC]"
          />
          <span>
            Select all {filteredStudents.length} visible
            {selectedIds.length > 0 && (
              <span className="ml-2 text-[#1E3F33] font-semibold">
                · {selectedIds.length} selected
              </span>
            )}
          </span>
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-2xl" />
          ))}
        </div>
      ) : view === "card" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="student-grid">
          {filteredStudents.map((s) => (
            <StudentCard
              key={s.id}
              student={s}
              myProviderId={provider?.id}
              onClick={() => navigate(`/students/${s.id}`)}
              onAssistantChange={handleAssistantChange}
              selectable={canBulkAct}
              selected={selectedIds.includes(s.id)}
              onToggleSelect={() => toggleSelect(s.id)}
            />
          ))}
        </div>
      ) : (
        <StudentTable
          students={filteredStudents}
          myProviderId={provider?.id}
          onClick={(id) => navigate(`/students/${id}`)}
          onAssistantChange={handleAssistantChange}
          selectable={canBulkAct}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleHeaderSort}
        />
      )}

      {/* FP3-058 — Bulk archive + delete modals. */}
      {canBulkAct && (
        <>
          <ArchiveSelectedModal
            open={archiveOpen}
            onOpenChange={setArchiveOpen}
            selectedIds={selectedIds}
            onSuccess={onBulkSuccess}
          />
          <DeleteSelectedModal
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            selectedIds={selectedIds}
            onSuccess={onBulkSuccess}
          />
          {/* FP3-064 — Bulk reassign students to a school/campus. */}
          <AssignCampusDialog
            open={assignCampusOpen}
            onOpenChange={setAssignCampusOpen}
            studentIds={selectedIds}
            schools={schools}
            onAssigned={onBulkSuccess}
          />
        </>
      )}

      {addOpen && (
        <AddStudentModal
          provider={provider}
          schools={schools}
          schoolsLoaded={schoolsLoaded}
          onClose={() => setAddOpen(false)}
          onCreated={() => {
            setAddOpen(false);
            refetch?.();
          }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// FP3-100 — Labeled toolbar cell: persistent small-caps label above
// an h-9 control so every filter/sort control aligns on one grid.
function ToolbarField({ label, htmlFor, children, className = "" }) {
  return (
    <div className={`flex flex-col gap-1 min-w-0 ${className}`}>
      <label
        htmlFor={htmlFor}
        className="text-[9px] font-semibold uppercase tracking-wider text-[#9B9890] pl-1 whitespace-nowrap"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

// FP3-100 — Sortable List-view column header. Clicking selects the
// column ascending; clicking again reverses. Synchronized with the
// toolbar Sort dropdown + direction toggle (one canonical state).
function CaseloadSortHeader({ id, label, sortBy, sortDir, onSort, testid }) {
  const active = sortBy === id;
  return (
    <button
      type="button"
      data-testid={testid}
      data-sort-active={active ? "true" : "false"}
      onClick={() => onSort?.(id)}
      title={`Sort by ${label}`}
      className={`flex items-center gap-0.5 min-w-0 text-left uppercase tracking-wider text-[10px] font-semibold transition-colors ${
        active ? "text-[#1E3F33]" : "text-[#4B5563] hover:text-[#1E3F33]"
      }`}
    >
      <span className="truncate">{label}</span>
      {active ? (
        sortDir === "desc" ? (
          <CaretDown size={11} weight="bold" className="shrink-0" data-testid={`${testid}-caret-desc`} />
        ) : (
          <CaretUp size={11} weight="bold" className="shrink-0" data-testid={`${testid}-caret-asc`} />
        )
      ) : (
        <CaretUp size={11} weight="bold" className="shrink-0 opacity-0" aria-hidden />
      )}
    </button>
  );
}

function ViewToggle({ view, setView }) {
  return (
    <div className="inline-flex items-center bg-white border border-[#E5E3DB] rounded-lg overflow-hidden" data-testid="view-toggle">
      {[
        { v: "card", label: "Card", Icon: SquaresFour },
        { v: "list", label: "List", Icon: ListBullets },
      ].map(({ v, label, Icon }) => (
        <button
          key={v}
          data-testid={`view-${v}`}
          onClick={() => setView(v)}
          className={`px-3 h-9 text-xs font-medium flex items-center gap-1.5 transition-colors ${
            view === v ? "bg-[#1E3F33] text-white" : "text-[#4B5563] hover:bg-[#F3F1EC]"
          }`}
        >
          <Icon size={13} weight="bold" />
          {label}
        </button>
      ))}
    </div>
  );
}

function StudentCard({
  student, onClick, onAssistantChange, myProviderId,
  selectable = false, selected = false, onToggleSelect,
}) {
  // Iter 33c — Primary Commitment (first service obligation, weekly or monthly)
  // + attendance pattern chip. Surfaces commitment volume + when the
  // student is on campus so providers can triage at a glance.
  const primaryCommitment = student.service_obligations?.[0];
  const patternLabel = PATTERN_LABEL_CASELOAD[normalizeAttendancePattern(student.attendance_pattern)] || null;
  // Pass C — show readable_id under the name
  return (
    <Card
      data-testid={`student-card-${student.id}`}
      data-bulk-selected={selected ? "true" : "false"}
      className={`bg-white rounded-2xl border ${
        selected ? "border-[#1E3F33] ring-2 ring-[#1E3F33]/20" : "border-[#E5E3DB]"
      } shadow-sm fp-card cursor-pointer overflow-hidden`}
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-3 min-w-0">
            {/* FP3-058 — Per-card selection checkbox. stopPropagation
                so toggling never opens the student detail page. */}
            {selectable && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="pt-0.5"
              >
                <Checkbox
                  data-testid={`student-card-select-${student.id}`}
                  checked={selected}
                  onCheckedChange={() => onToggleSelect?.()}
                  className="border-[#D4D2CC]"
                />
              </div>
            )}
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-[#1F2937] heading-font">
                {student.first_name} {student.last_name}
              </h3>
              {student.readable_id && (
                <p
                  data-testid={`card-readable-id-${student.id}`}
                  className="text-[9px] font-mono text-[#9B9890] tabular-nums mt-0.5"
                >
                  {student.readable_id}
                </p>
              )}
              <p className="text-xs text-[#6B7280] mt-0.5">
                Grade {student.grade} · {student.campus?.split(" ")[0]}
                {student.teacher && <span className="ml-1.5 text-[#9B9890]">· {student.teacher}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {/* FP3-096 — Compact canonical Eligibility chip near Grade
                + Student Status. Same resolver as Admin. */}
            <EligibilityChip student={student} testidPrefix="card-elig" />
            {patternLabel && (
              <Badge
                data-testid={`student-pattern-${student.id}`}
                className={`text-[10px] capitalize hover:opacity-90 ${PATTERN_CHIP_CLASS[normalizeAttendancePattern(student.attendance_pattern)] || "bg-[#E8F0EB] text-[#1E3F33]"}`}
              >
                {patternLabel}
              </Badge>
            )}
            {student.status && student.status !== "active" && (
              <Badge className="bg-[#EDF2F7] text-[#4A6B8C] text-[10px] hover:bg-[#EDF2F7] capitalize">
                {student.status}
              </Badge>
            )}
            {MY_SERVICE_CHIP[myAssignmentStatus(student, myProviderId)] && (
              <Badge
                data-testid={`my-service-chip-${student.id}`}
                className={`text-[10px] hover:opacity-90 ${MY_SERVICE_CHIP[myAssignmentStatus(student, myProviderId)].cls}`}
              >
                {MY_SERVICE_CHIP[myAssignmentStatus(student, myProviderId)].label}
              </Badge>
            )}
            <ArrowRight size={14} className="text-[#6B7280] ml-1" />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {/* FP3-095 — Student's canonical Areas of Impact (all areas,
              same resolver + chips as Admin Students). Replaces the
              legacy provider-narrowed service chips. */}
          <ImpactChips student={student} max={2} testidPrefix="card-impact" />
          {/* FP3-029B6C — Inline assistant assignment chip. Renders
              only when the signed-in primary supervises ≥1 active
              assistant. Click → small picker → assigns/reassigns/
              removes. Calls POST /students/{id}/route under the hood. */}
          <AssistantAssignmentChip
            student={student}
            variant="card"
            onChange={onAssistantChange}
          />
          {/* FP3-069 follow-up #11 — Read-only "Assigned to Assistant"
              status pill for non-supervising viewers (admin,
              super_admin, assistant, non-supervising primary). The
              pill auto-suppresses when the AssistantAssignmentChip
              above is already shown to the caller. */}
          <AssistantCoveredPill student={student} variant="card" />
          {/* FP3-029C — Tag covering provider's caseload rows so it's
              obvious which students are temporary coverage versus
              permanent caseload. */}
          <CoverageBadge student={student} variant="card" />
        </div>
        <div className="flex items-center gap-4 text-xs text-[#6B7280] flex-wrap">
          <span className="flex items-center gap-1" data-testid={`student-commitment-${student.id}`}>
            <Clock size={13} weight="duotone" className="text-[#4A6B8C]" />
            {primaryCommitment
              ? `${primaryCommitment.total_minutes} min/${primaryCommitment.kind === "minutes_per_week" ? "wk" : "mo"}`
              : "No commitment"}
          </span>
          <span className="flex items-center gap-1">
            <Target size={13} weight="duotone" className="text-[#8FA998]" />
            {student.active_goal_count || 0} goals
          </span>
          {student.next_session && (
            <span className="flex items-center gap-1">
              <CalendarBlank size={13} weight="duotone" className="text-[#4A6B8C]" />
              Next: {student.next_session.date}
            </span>
          )}
        </div>
        {student.alerts?.length > 0 && (
          <div className="mt-3 space-y-1">
            {student.alerts.map((alert, i) => (
              <div
                key={i}
                className={`flex items-center gap-1.5 text-[11px] font-medium rounded px-2 py-1 ${
                  alert.type === "overdue"
                    ? "bg-[#FDF2F2] text-[#B85C5C]"
                    : "bg-[#FDF8F3] text-[#D9A05B]"
                }`}
              >
                <Warning size={12} weight="fill" />
                {alert.message}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// FP3-100 — display labels keyed by the CANONICAL pattern token; all
// lookups normalize first (legacy half_day_am / variable etc.) so a
// row can never sort under AM while displaying "—".
const PATTERN_LABEL_CASELOAD = {
  full_day: "Full Day",
  am: "AM",
  pm: "PM",
  partial: "Partial",
};

const PATTERN_CHIP_CLASS = {
  full_day: "bg-[#E8F0EB] text-[#1E3F33]",
  am: "bg-[#FDF8F3] text-[#D9A05B]",
  pm: "bg-[#EDF2F7] text-[#4A6B8C]",
  partial: "bg-[#F3F1EC] text-[#6B7280]",
};

// FP3-097 — Teacher-name derivation now comes from the SHARED
// `@/lib/teacherIdentity` module (same one Service Demographics and
// the Admin Profile tab use) so no screen derives independently.
const deriveTeacherDisplayLocal = deriveTeacherDisplay;

function StudentTable({
  students, onClick, onAssistantChange, myProviderId,
  selectable = false, selectedIds = [], onToggleSelect,
  sortBy, sortDir, onSort,
}) {
  // FP3-058 — When the caller is allowed to bulk-act, a checkbox
  // column is prepended to both header and rows. Click bubbling on
  // the checkbox is stopped so toggling never navigates.
  //
  // FP3-112A — Column widths use pixel/minmax caps so the table
  // stays compact even inside the wider FP3-112 PageShell (up to
  // 1600-1760px). The old spec used three `1fr` columns
  // (Teacher/Campus/Areas), which meant every extra pixel of shell
  // width was distributed as inter-column gap, leaving huge empty
  // bands between values. New spec:
  //   • compact columns: fixed px (Grade / Eligibility / Pattern /
  //     Commitment / Goals / row-action) so they never over-grow;
  //   • flexible columns: `minmax(min, max)` so Name / Teacher /
  //     Campus / Areas / Assistant can grow modestly with the
  //     viewport but stop at a readable cap — anything extra
  //     becomes white space to the right of the table rather than
  //     between columns.
  const cols = selectable
    ? "30px minmax(160px,240px) 50px 65px 78px 95px minmax(120px,180px) minmax(100px,160px) minmax(140px,220px) 50px minmax(130px,170px) 24px"
    : "minmax(160px,240px) 50px 65px 78px 95px minmax(120px,180px) minmax(100px,160px) minmax(140px,220px) 50px minmax(130px,170px) 24px";
  return (
    <div
      data-testid="student-list"
      className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm overflow-hidden max-w-[1400px]"
    >
      {/* FP3-096 — Horizontal scroll at narrow widths so the preferred
          student-context columns (Name / Grade / Eligibility) are never
          silently collapsed; min-w keeps every column readable.
          FP3-112A — Reduced min-w from 1080 to 1024 (new column mins
          add to ~1012 + 80 gap-2 = 1092; a slightly lower floor lets
          the contained scroll engage only at genuinely narrow widths). */}
      <div className="overflow-x-auto">
        <div className="min-w-[1024px]">
      <div
        className="grid gap-2 px-4 py-2.5 bg-[#F3F1EC] text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]"
        style={{ gridTemplateColumns: cols }}
      >
        {selectable && <span></span>}
        {/* FP3-100 — Sortable headers (Name / Grade / Eligibility /
            Pattern / Commitment / Teacher / Campus / Assistant).
            Areas of Impact and Goals are deliberately NOT sortable. */}
        <CaseloadSortHeader id="name" label="Name" sortBy={sortBy} sortDir={sortDir} onSort={onSort} testid="caseload-sort-name" />
        <CaseloadSortHeader id="grade" label="Grade" sortBy={sortBy} sortDir={sortDir} onSort={onSort} testid="caseload-sort-grade" />
        <CaseloadSortHeader id="eligibility" label="Eligibility" sortBy={sortBy} sortDir={sortDir} onSort={onSort} testid="caseload-sort-eligibility" />
        <CaseloadSortHeader id="pattern" label="Pattern" sortBy={sortBy} sortDir={sortDir} onSort={onSort} testid="caseload-sort-pattern" />
        <CaseloadSortHeader id="commitment" label="Commitment" sortBy={sortBy} sortDir={sortDir} onSort={onSort} testid="caseload-sort-commitment" />
        <CaseloadSortHeader id="teacher" label="Teacher" sortBy={sortBy} sortDir={sortDir} onSort={onSort} testid="caseload-sort-teacher" />
        <CaseloadSortHeader id="campus" label="Campus" sortBy={sortBy} sortDir={sortDir} onSort={onSort} testid="caseload-sort-campus" />
        <span data-testid="caseload-col-areas-of-impact">Areas of impact</span>
        <span>Goals</span>
        <CaseloadSortHeader id="assistant" label="Assistant" sortBy={sortBy} sortDir={sortDir} onSort={onSort} testid="caseload-sort-assistant" />
        <span></span>
      </div>
      <div className="divide-y divide-[#ECEAE4]">
        {students.map((s) => {
          const pc = s.service_obligations?.[0];
          const patternLabel = PATTERN_LABEL_CASELOAD[normalizeAttendancePattern(s.attendance_pattern)] || "—";
          const isSelected = selectable && selectedIds.includes(s.id);
          return (
            <div
              key={s.id}
              data-testid={`student-row-${s.id}`}
              data-bulk-selected={isSelected ? "true" : "false"}
              onClick={() => onClick(s.id)}
              className={`grid gap-2 px-4 py-3 items-center text-xs cursor-pointer transition-colors ${
                isSelected ? "bg-[#F1F8F3] hover:bg-[#E8F0EB]" : "hover:bg-[#F9F8F6]"
              }`}
              style={{ gridTemplateColumns: cols }}
            >
              {selectable && (
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    data-testid={`student-row-select-${s.id}`}
                    checked={isSelected}
                    onCheckedChange={() => onToggleSelect?.(s.id)}
                    className="border-[#D4D2CC]"
                  />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#1F2937] truncate">
                  {s.first_name} {s.last_name}
                </p>
                <div className="flex items-center gap-1.5 text-[10px] text-[#9B9890]">
                  {s.readable_id && (
                    <span data-testid={`row-readable-id-${s.id}`} className="font-mono tabular-nums">
                      {s.readable_id}
                    </span>
                  )}
                  {s.status && s.status !== "active" && (
                    <span className="capitalize">· {s.status}</span>
                  )}
                  {MY_SERVICE_CHIP[myAssignmentStatus(s, myProviderId)] && (
                    <span
                      data-testid={`my-service-row-chip-${s.id}`}
                      className={`px-1.5 py-0.5 rounded-full ${MY_SERVICE_CHIP[myAssignmentStatus(s, myProviderId)].cls}`}
                    >
                      {MY_SERVICE_CHIP[myAssignmentStatus(s, myProviderId)].label}
                    </span>
                  )}
                </div>
              </div>
              <span>Grade {s.grade}</span>
              <span data-testid={`caseload-elig-cell-${s.id}`}>
                <EligibilityChip student={s} testidPrefix="caseload-elig" />
              </span>
              <span>
                <span
                  data-testid={`student-row-pattern-${s.id}`}
                  className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                    PATTERN_CHIP_CLASS[normalizeAttendancePattern(s.attendance_pattern)] || "bg-[#F3F1EC] text-[#6B7280]"
                  }`}
                >
                  {patternLabel}
                </span>
              </span>
              <span data-testid={`student-row-commitment-${s.id}`} className="text-[#4B5563] tabular-nums">
                {pc
                  ? `${pc.total_minutes} min/${pc.kind === "minutes_per_week" ? "wk" : "mo"}`
                  : "—"}
              </span>
              <span className="truncate text-[#4B5563]">{s.teacher || "—"}</span>
              <span className="truncate text-[#4B5563]">{s.campus}</span>
              <span className="flex flex-wrap gap-1" data-testid={`caseload-impact-cell-${s.id}`}>
                {/* FP3-095 — Canonical Areas of Impact (up to 2 chips
                    + "+N" tooltip), identical to Admin Students. */}
                <ImpactChips student={s} max={2} testidPrefix="caseload-impact" />
              </span>
              <span className="flex items-center gap-1 text-[#1E3F33] font-medium">
                <Target size={11} weight="duotone" />
                {s.active_goal_count || 0}
              </span>
              {/* FP3-029B6C — Assistant assignment column. Renders
                  the inline picker only for supervising primaries. */}
              <span className="flex flex-col items-start gap-1">
                <AssistantAssignmentChip
                  student={s}
                  variant="row"
                  onChange={onAssistantChange}
                />
                {/* FP3-069 follow-up #11 — Read-only "Assigned to
                    Assistant" pill for non-supervising viewers. */}
                <AssistantCoveredPill student={s} variant="row" />
                {/* FP3-029C — Temporary-coverage indicator on covering
                    provider's caseload rows. */}
                <CoverageBadge student={s} variant="row" />
              </span>
              <ArrowRight size={13} className="text-[#9B9890]" />
            </div>
          );
        })}
        {students.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-[#6B7280]">
            No students match your filters.
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
}

// FP3-035B-1 — Age in years;months from DOB. Returns "" if DOB
// is missing/malformed. Used by Add Student forms.
function computeAgeForDob(dobIso) {
  if (!dobIso || typeof dobIso !== "string" || dobIso.length < 10) return "";
  const [by, bm, bd] = dobIso.slice(0, 10).split("-").map((n) => parseInt(n, 10));
  if (!by || !bm || !bd) return "";
  const now = new Date();
  let years = now.getFullYear() - by;
  let months = now.getMonth() + 1 - bm;
  if (now.getDate() < bd) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  if (years < 0) return "";
  return `${years};${months}`;
}


function AddStudentModal({ provider, schools = [], schoolsLoaded = false, onClose, onCreated }) {
  // FP3-078 — Pull the auth-context gate so solo Add Student goes
  // through the same authorization checkpoint as the admin path.
  const { ensureStudentDataAck } = useAuth();
  // Iter 33d — default teacher domain: prefer provider.default_teacher_domain,
  // else parse the provider's own email. Keep a stable suffix hint in the
  // input so providers only type the local part.
  const providerEmail = provider?.email || "";
  const derivedDomain = providerEmail.includes("@")
    ? providerEmail.split("@", 2)[1]
    : "";
  const savedDomain = provider?.default_teacher_domain || derivedDomain || "";
  // FP3-079E — Inline "Change default domain" control. The override
  // persists via PATCH /providers/{id}/settings and takes effect
  // immediately in this modal; existing saved teacher emails are
  // never rewritten.
  const [domainOverride, setDomainOverride] = useState(null);
  const defaultDomain = domainOverride ?? savedDomain;
  const [editingDomain, setEditingDomain] = useState(false);
  const [domainDraft, setDomainDraft] = useState("");
  const [savingDomain, setSavingDomain] = useState(false);

  const saveDomain = async () => {
    const clean = domainDraft.trim().replace(/^@+/, "").toLowerCase();
    if (!clean || !clean.includes(".")) {
      toast.error("Enter a valid domain, e.g. school.edu");
      return;
    }
    setSavingDomain(true);
    try {
      await api.updateProviderSettings(provider.id, { default_teacher_domain: clean });
      setDomainOverride(clean);
      setEditingDomain(false);
      toast.success(`Default teacher email domain set to @${clean}`);
    } catch (e) {
      toast.error(`Could not save domain: ${prettyApiError(e)}`);
    } finally {
      setSavingDomain(false);
    }
  };
  // FP3-043 Phase A follow-up #7 — Campus options come from the
  // provider's org-scoped schools list (passed in from CaseloadList).
  // We narrow to the provider's `assigned_schools` first; if they
  // have no assigned schools, the provider sees every school in
  // their organization. NO demo fallback ever surfaces.
  const assignedSchoolIds = Array.isArray(provider?.school_ids) ? provider.school_ids : [];
  const visibleSchools = useMemo(() => {
    if (assignedSchoolIds.length > 0) {
      const allowed = new Set(assignedSchoolIds);
      const narrowed = schools.filter((s) => allowed.has(s.id));
      // If narrowing produced zero rows (provider's assigned_schools
      // are stale), fall back to the org-wide list so the operator
      // can still create the student.
      return narrowed.length > 0 ? narrowed : schools;
    }
    return schools;
  }, [schools, assignedSchoolIds]);

  const [draft, setDraft] = useState({
    first_name: "",
    last_name: "",
    grade: "0",
    campus: "",
    school_id: "",
    teacher: "",
    teacher_email_local: "",
    // FP3-045 — Student identity fields. Optional; normalized
    // server-side (student_email lowercase + trim, external_id trim).
    external_id: "",
    student_email: "",
    attendance_pattern: "full_day",
    services: [],   // Pass B — canonical {key, label, domain} entries
    assigned_providers: provider ? [provider.id] : [],
    status: "active", // Iter 27 — selectable initial status
    // FP3-035B-1 — Platform demographic + eligibility on Add Student.
    date_of_birth: "",
    sex: "",
    eligibility_categories: [],  // FP3-035B-1 correction — multi-eligibility array
    lre: "",                     // FP3-035B-1 follow-up — Least Restrictive Environment
    iep_due_date: "",
    met_due_date: "",
  });
  // FP3-043 Phase A follow-up #7 — Auto-select the single available
  // campus once schools have loaded (or once narrowing settles).
  useEffect(() => {
    if (!schoolsLoaded) return;
    if (draft.school_id) return;
    if (visibleSchools.length === 1) {
      const only = visibleSchools[0];
      setDraft((d) => ({ ...d, school_id: only.id, campus: only.name }));
    }
  }, [schoolsLoaded, visibleSchools, draft.school_id]);
  // FP3-035B-1 — Eligibility taxonomy.
  const [eligibilityCategories, setEligibilityCategories] = useState([]);
  // FP3-035B-1 follow-up — LRE taxonomy.
  const [lreLevels, setLreLevels] = useState([]);
  useEffect(() => {
    api.getEligibilityCategories()
      .then((r) => setEligibilityCategories(r.data?.categories || []))
      .catch(() => setEligibilityCategories([]));
    api.getLreLevels()
      .then((r) => setLreLevels(r.data?.levels || []))
      .catch(() => setLreLevels([]));
  }, []);
  const ageHint = computeAgeForDob(draft.date_of_birth);
  const [saving, setSaving] = useState(false);
  // Iter 34 Pass B — role-aware taxonomy. Role-scoped services render as
  // primary chips; "Show all AZ services" expands to the full master
  // taxonomy for atypical multi-domain caseloads.
  const [taxonomy, setTaxonomy] = useState(null);
  const [showAllServices, setShowAllServices] = useState(false);
  useEffect(() => {
    api.getServicesTaxonomy({ role: provider?.role || "" })
      .then((r) => setTaxonomy(r.data))
      .catch(() => setTaxonomy(null));
  }, [provider?.role]);

  const scopedServices = taxonomy?.scoped?.services || [];
  const allServices = taxonomy?.services || [];
  const visibleServices = showAllServices ? allServices : scopedServices;

  const toggleService = (svc) =>
    setDraft((d) => {
      const exists = d.services.find((s) => s.key === svc.key);
      return {
        ...d,
        services: exists
          ? d.services.filter((s) => s.key !== svc.key)
          : [...d.services, { key: svc.key, label: svc.label, domain: svc.domain, categories: svc.categories || [] }],
      };
    });

  const assembleEmail = () => {
    const local = draft.teacher_email_local.trim();
    if (!local) return "";
    if (local.includes("@")) return local.toLowerCase(); // user typed full email
    return defaultDomain ? `${local}@${defaultDomain}`.toLowerCase() : "";
  };

  const submit = async () => {
    if (!draft.first_name.trim() || !draft.last_name.trim()) {
      return toast.error("Name is required");
    }
    const email = assembleEmail();
    if (!email || !email.includes("@")) {
      return toast.error("Teacher email is required");
    }
    // FP3-043 Phase A follow-up #7 — Require a campus when the org
    // has any campuses to choose from. Orgs with zero campuses can
    // still create (operator already saw the empty-state hint).
    if (visibleSchools.length > 0 && !draft.school_id) {
      return toast.error("Pick a campus before creating the student");
    }
    setSaving(true);
    try {
      // FP3-078 — Student-data authorization checkpoint. Resolves
      // immediately for already-acknowledged users; otherwise pops
      // the modal and waits for confirmation before the API write.
      const ok = await ensureStudentDataAck();
      if (!ok) {
        setSaving(false);
        return;
      }
      await api.createStudent({
        first_name: draft.first_name,
        last_name: draft.last_name,
        grade: draft.grade,
        campus: draft.campus,
        // FP3-043 Phase A follow-up #7 — Pass school_id directly so
        // /admin/schools counts the new student under the correct
        // campus immediately (no reliance on the campus-name
        // resolver fallback).
        school_id: draft.school_id || null,
        teacher: draft.teacher || deriveTeacherDisplayLocal(email),
        teacher_email: email,
        // FP3-045 — Identity fields. Optional; backend normalizes.
        external_id: (draft.external_id || "").trim(),
        student_email: (draft.student_email || "").trim(),
        attendance_pattern: draft.attendance_pattern,
        // Pass B — send canonical services; backend keeps a synced
        // service_areas[] from the labels for legacy readers.
        services: draft.services,
        service_areas: draft.services.map((s) => s.label),
        assigned_providers: draft.assigned_providers,
        status: draft.status,
        // FP3-035B-1 — Platform demographics + eligibility on create.
        date_of_birth: draft.date_of_birth || null,
        sex: draft.sex || "",
        // FP3-035B-1 correction — Multi-eligibility (+ legacy single).
        eligibility_category: primaryEligibilityKey(draft.eligibility_categories),
        eligibility_categories: (draft.eligibility_categories || []).filter((e) => e.category),
        // FP3-035B-1 follow-up — LRE.
        lre: draft.lre || "",
        iep_due_date: draft.iep_due_date || null,
        met_due_date: draft.met_due_date || null,
      });
      toast.success("Student added");
      onCreated();
    } catch (e) {
      toast.error(`Failed: ${prettyApiError(e)}`);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      data-testid="add-student-modal"
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-[#E5E3DB] flex items-center justify-between shrink-0">
          <h2 className="text-lg font-semibold text-[#1F2937] heading-font">Add Student</h2>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#1F2937]" data-testid="add-student-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3 overflow-y-auto">
          <Field label="First name *">
            <Input
              value={draft.first_name}
              onChange={(e) => setDraft({ ...draft, first_name: e.target.value })}
              data-testid="add-student-first"
              className="h-9 text-sm"
            />
          </Field>
          <Field label="Last name *">
            <Input
              value={draft.last_name}
              onChange={(e) => setDraft({ ...draft, last_name: e.target.value })}
              data-testid="add-student-last"
              className="h-9 text-sm"
            />
          </Field>
          <Field label="Grade">
            <select
              value={draft.grade}
              onChange={(e) => setDraft({ ...draft, grade: e.target.value })}
              data-testid="add-student-grade"
              className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
            >
              {nativeOptions(GRADES, (g) => ({ value: g, label: `Grade ${g}` }))}
            </select>
          </Field>
          <Field label="Campus">
            <select
              value={draft.school_id}
              onChange={(e) => {
                const sid = e.target.value;
                const match = visibleSchools.find((s) => s.id === sid);
                setDraft({
                  ...draft,
                  school_id: sid,
                  campus: match ? match.name : "",
                });
              }}
              disabled={schoolsLoaded && visibleSchools.length === 0}
              data-testid="add-student-campus"
              className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full disabled:bg-[#F3F1EC] disabled:text-[#9B9890]"
            >
              {/* FP3-043 Phase A follow-up #7 — Real org-scoped
                  campuses only. No demo fallback. */}
              {visibleSchools.length === 0 ? (
                <option value="">
                  {schoolsLoaded
                    ? "No campuses available — add a school first"
                    : "Loading campuses…"}
                </option>
              ) : (
                <>
                  {visibleSchools.length > 1 && (
                    <option value="" data-testid="add-student-campus-blank">
                      Select a campus…
                    </option>
                  )}
                  {visibleSchools.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </>
              )}
            </select>
            {schoolsLoaded && visibleSchools.length === 0 && (
              <p
                className="text-[10px] text-[#9B9890] italic mt-1"
                data-testid="add-student-campus-empty-hint"
              >
                No campuses available in your organization. Ask an admin
                to add a school before creating students.
              </p>
            )}
          </Field>
          <Field label="Teacher email *">
            {defaultDomain ? (
              <div className="flex items-stretch">
                <Input
                  value={draft.teacher_email_local}
                  onChange={(e) => setDraft({ ...draft, teacher_email_local: e.target.value })}
                  data-testid="add-student-teacher-email-local"
                  placeholder="p.patel"
                  className="h-9 text-sm rounded-r-none"
                />
                <span
                  data-testid="add-student-teacher-email-suffix"
                  className="inline-flex items-center px-2 text-xs text-[#4B5563] bg-[#F3F1EC] border border-l-0 border-[#d4d2cc] rounded-r-lg whitespace-nowrap"
                >
                  @{defaultDomain}
                </span>
              </div>
            ) : (
              <Input
                type="email"
                value={draft.teacher_email_local}
                onChange={(e) => setDraft({ ...draft, teacher_email_local: e.target.value })}
                data-testid="add-student-teacher-email-local"
                placeholder="p.patel@school.edu"
                className="h-9 text-sm"
              />
            )}
            <div className="flex items-center justify-between gap-2 mt-1">
              <p className="text-[10px] text-[#9B9890]">
                Required. Used for teacher-dependent scheduling + reminders.
              </p>
              {provider?.id && !editingDomain && (
                <button
                  type="button"
                  data-testid="add-student-change-domain-btn"
                  onClick={() => {
                    setDomainDraft(defaultDomain || "");
                    setEditingDomain(true);
                  }}
                  className="text-[10px] text-[#1E3F33] underline underline-offset-2 whitespace-nowrap hover:text-[#2F5D4A]"
                >
                  {defaultDomain ? `Using @${defaultDomain} · Change` : "Set default domain"}
                </button>
              )}
            </div>
            {editingDomain && (
              <div className="flex items-center gap-1.5 mt-1.5" data-testid="add-student-domain-editor">
                <span className="text-xs text-[#4B5563]">@</span>
                <Input
                  value={domainDraft}
                  onChange={(e) => setDomainDraft(e.target.value)}
                  data-testid="add-student-domain-input"
                  placeholder="school.edu"
                  className="h-7 text-xs flex-1"
                  autoComplete="off"
                  spellCheck={false}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={saveDomain}
                  disabled={savingDomain}
                  data-testid="add-student-domain-save-btn"
                  className="h-7 text-[11px] bg-[#1E3F33] hover:bg-[#2F5D4A] text-white"
                >
                  {savingDomain ? "Saving…" : "Save"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setEditingDomain(false)}
                  data-testid="add-student-domain-cancel-btn"
                  className="h-7 text-[11px]"
                >
                  Cancel
                </Button>
              </div>
            )}
          </Field>
          {/* FP3-045 — Student identity fields. Both optional. */}
          <Field label="External Student ID">
            <Input
              value={draft.external_id}
              onChange={(e) => setDraft({ ...draft, external_id: e.target.value })}
              data-testid="add-student-external-id"
              placeholder="e.g. SIS-12345"
              className="h-9 text-sm"
            />
            <p className="text-[10px] text-[#9B9890] mt-1">
              Optional. Use the school or district&apos;s local student ID if available.
            </p>
          </Field>
          <Field label="Student Email">
            <Input
              type="email"
              value={draft.student_email}
              onChange={(e) => setDraft({ ...draft, student_email: e.target.value })}
              data-testid="add-student-student-email"
              placeholder="student@school.edu"
              className="h-9 text-sm"
            />
            <p className="text-[10px] text-[#9B9890] mt-1">
              Optional. Used by some schools as a student identifier and for future integrations.
            </p>
          </Field>
          <Field label="Attendance">
            <select
              value={draft.attendance_pattern}
              onChange={(e) => setDraft({ ...draft, attendance_pattern: e.target.value })}
              data-testid="add-student-pattern"
              className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
            >
              <option value="full_day">Full Day</option>
              <option value="am">AM</option>
              <option value="pm">PM</option>
            </select>
          </Field>
          <Field label="Service Status">
            <select
              value={draft.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value })}
              data-testid="add-student-status"
              className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
            >
              {/* FP3-070 follow-up — Canonical Service Status options.
                  Archived is intentionally NOT in the create modal —
                  it doesn't make sense to create a student already
                  archived. Use Service Demographics to archive later. */}
              <option value="active">IEP</option>
              <option value="rti">RTI</option>
              <option value="monitoring">Monitoring</option>
              <option value="graduated">Graduated</option>
            </select>
          </Field>
          {/* FP3-035B-1 — Platform demographic + eligibility fields.
              DOB and Sex are platform-wide. FP3-080 — Sex is also the
              source of truth for articulation norm comparisons; the
              separate Speech Norm Category field was removed.
              Eligibility Category is the official school category,
              separate from Service Areas. */}
          <Field label="Date of birth">
            <Input
              type="date"
              value={draft.date_of_birth}
              onChange={(e) => setDraft({ ...draft, date_of_birth: e.target.value })}
              data-testid="add-student-dob"
              className="h-9 text-sm"
            />
            {ageHint && (
              <p className="text-[10px] text-[#6B7280] mt-1" data-testid="add-student-age-hint">
                Age <span className="text-[#1F2937] font-medium">{ageHint}</span> (years;months)
              </p>
            )}
          </Field>
          <Field label="Sex">
            <select
              value={draft.sex}
              onChange={(e) => setDraft({ ...draft, sex: e.target.value })}
              data-testid="add-student-sex"
              className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
            >
              <option value="">Not specified</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="other">Other</option>
            </select>
          </Field>
          {/* FP3-035B-1 correction — Multi-eligibility editor. Up to 4
              entries; primary required when any are set. SLD / DD / PSD
              reveal subcategory checkboxes. Spans both columns so wide
              taxonomy labels render cleanly. */}
          <div className="col-span-2 space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]">
              Eligibility categories
            </p>
            <EligibilityEditor
              value={draft.eligibility_categories}
              onChange={(next) => setDraft({ ...draft, eligibility_categories: next })}
              taxonomy={eligibilityCategories}
              testIdPrefix="add-student-elig"
            />
            <p className="text-[10px] text-[#9B9890] italic">
              Up to 4 entries. Primary required when any eligibility is set.
              Official school category — separate from service areas.
            </p>
          </div>
          <Field label="IEP due date">
            <Input
              type="date"
              value={draft.iep_due_date}
              onChange={(e) => setDraft({ ...draft, iep_due_date: e.target.value })}
              data-testid="add-student-iep-due"
              className="h-9 text-sm"
            />
          </Field>
          <Field label="MET / Evaluation due date">
            <Input
              type="date"
              value={draft.met_due_date}
              onChange={(e) => setDraft({ ...draft, met_due_date: e.target.value })}
              data-testid="add-student-met-due"
              className="h-9 text-sm"
            />
          </Field>
          {/* FP3-035B-1 follow-up — LRE on intake. Canonical levels
              (A/B/C/D/E/H/J); empty = unset / not specified. */}
          <Field label="LRE (Least Restrictive Environment)">
            <select
              value={draft.lre || ""}
              onChange={(e) => setDraft({ ...draft, lre: e.target.value })}
              data-testid="add-student-lre"
              className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
            >
              <option value="">Not specified</option>
              {lreLevels.map((lvl) => (
                <option key={lvl.key} value={lvl.key}>{lvl.label}</option>
              ))}
            </select>
          </Field>
          {/* FP3-080 — Speech Norm Category field removed. Articulation
              norm comparisons use the Sex field above. */}
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]">
                Service areas
                {provider?.role && !showAllServices && (
                  <span className="ml-1.5 text-[10px] normal-case font-medium text-[#9B9890]">· {provider.role} default</span>
                )}
              </p>
              {allServices.length > scopedServices.length && (
                <button
                  type="button"
                  data-testid="add-student-toggle-all-services"
                  onClick={() => setShowAllServices((v) => !v)}
                  className="text-[10px] text-[#3F4756] hover:text-[#1E3F33] underline"
                >
                  {showAllServices ? "Show role default" : `Show all (${allServices.length})`}
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="add-student-services">
              {visibleServices.length === 0 && (
                <span className="text-[11px] text-[#9B9890] italic">Loading taxonomy…</span>
              )}
              {visibleServices.map((svc) => {
                const selected = draft.services.find((s) => s.key === svc.key);
                return (
                  <button
                    key={svc.key}
                    type="button"
                    onClick={() => toggleService(svc)}
                    data-testid={`add-student-svc-${svc.key}`}
                    title={svc.categories?.includes("sped") ? "SpEd service" : "Related service"}
                    className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
                      selected
                        ? "bg-[#1E3F33] text-white"
                        : "bg-[#E1EAE4] text-[#1E3F33] hover:bg-[#d5e5d9]"
                    }`}
                  >
                    {svc.label}
                  </button>
                );
              })}
            </div>
            {draft.services.length > 0 && (
              <p className="text-[10px] text-[#6B7280] mt-1.5">
                Selected: {draft.services.map((s) => s.label).join(", ")}
              </p>
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-[#E5E3DB] flex justify-end gap-2 bg-[#FAFAF7] shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-lg h-9 text-sm border-[#d4d2cc]"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="add-student-submit"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-9 text-sm"
          >
            {saving ? "Saving…" : "Add Student"}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">
        {label}
      </p>
      {children}
    </div>
  );
}
